import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { LayoutDashboard, Settings, ScrollText, LogOut, Users, Gift, Star, Menu, X, Shield, UserCircle, Edit3, ShieldAlert, Target, BadgeCheck, Handshake } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import AdminAlerts from "@/components/AdminAlerts";

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !["admin", "viewer", "operator"].includes(user.role))) {
      navigate("/admin/login");
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center text-gray-500">Chargement…</div>;
  }

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
      isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
    }`;

  // Close drawer after navigation on mobile
  const closeOnNav = () => setOpen(false);

  // Operators focus on processing operations — hide management-only sections.
  const isOperator = user.role === "operator";

  const Sidebar = (
    <>
      <Link to="/admin" className="flex items-center gap-2.5 mb-8 text-white" onClick={closeOnNav}>
        <img
          src="/brand/swiftlink-logo.png"
          alt="Swiftlink-Africa"
          className="h-11 w-11 object-contain rounded-md bg-white"
        />
        <span className="font-heading text-lg font-bold">Swiftlink-Africa</span>
      </Link>
      <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Administration</div>
      <nav className="flex-1 space-y-1">
        <NavLink to="/admin" end className={linkCls} onClick={closeOnNav} data-testid="admin-nav-dashboard">
          <LayoutDashboard size={18} /> Dashboard
        </NavLink>
        {!isOperator && (
          <NavLink to="/admin/agents" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-agents">
            <Users size={18} /> Agents
          </NavLink>
        )}
        <NavLink to="/admin/utilisateurs" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-users">
          <UserCircle size={18} /> Utilisateurs
        </NavLink>
        {!isOperator && (
          <NavLink to="/admin/administrateurs" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-admins">
            <Shield size={18} /> Administrateurs
          </NavLink>
        )}
        {!isOperator && (
          <NavLink to="/admin/parrainage" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-referrals">
            <Gift size={18} /> Parrainage
          </NavLink>
        )}
        {!isOperator && (
          <NavLink to="/admin/missions" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-missions">
            <Target size={18} /> Missions
          </NavLink>
        )}
        {!isOperator && (
          <NavLink to="/admin/verifications" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-verifications">
            <BadgeCheck size={18} /> Vérifications
          </NavLink>
        )}
        {!isOperator && (
          <NavLink to="/admin/partenaires" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-partners">
            <Handshake size={18} /> Partenaires
          </NavLink>
        )}
        <NavLink to="/admin/avis" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-reviews">
          <Star size={18} /> Avis clients
        </NavLink>
        {!isOperator && (
          <NavLink to="/admin/parametres" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-settings">
            <Settings size={18} /> Paramètres
          </NavLink>
        )}
        {!isOperator && (
          <NavLink to="/admin/contenu" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-content">
            <Edit3 size={18} /> Contenu du site
          </NavLink>
        )}
        <NavLink to="/admin/fraude" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-fraud">
          <ShieldAlert size={18} /> Détection fraude
        </NavLink>
        <NavLink to="/admin/logs" className={linkCls} onClick={closeOnNav} data-testid="admin-nav-logs">
          <ScrollText size={18} /> Journal d&apos;actions
        </NavLink>
      </nav>
      <div className="mt-auto pt-6 border-t border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-white/50">Thème</span>
          <ThemeToggle />
        </div>
        <div className="text-xs text-white/50 mb-2">Connecté en tant que</div>
        <div className="text-sm text-white mb-3 truncate">{user.email}</div>
        <button
          onClick={async () => { await logout(); navigate("/admin/login"); }}
          className="w-full inline-flex items-center justify-center gap-2 text-sm py-2 px-3 rounded-lg bg-white/10 text-white hover:bg-white/15"
          data-testid="admin-logout-btn"
        >
          <LogOut size={16} /> Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[var(--sl-bg-alt)]">
      {/* Mobile top bar */}
      <header
        className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3 text-white shadow-md"
        style={{ background: "var(--sl-primary-dark)" }}
        data-testid="admin-mobile-topbar"
      >
        <Link to="/admin" className="flex items-center gap-2">
          <img src="/brand/swiftlink-logo.png" alt="Swiftlink-Africa" className="h-8 w-8 object-contain rounded bg-white" />
          <span className="font-heading text-sm font-bold">Swiftlink-Africa</span>
        </Link>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="p-2 rounded-lg hover:bg-white/10"
            aria-label="Ouvrir le menu"
            data-testid="admin-mobile-menu-open"
          >
            <Menu size={20} />
          </button>
        </div>
      </header>

      {/* Desktop sidebar (always visible) */}
      <aside
        className="hidden lg:flex w-64 fixed left-0 top-0 h-screen flex-col p-5"
        style={{ background: "var(--sl-primary-dark)" }}
        data-testid="admin-sidebar"
      >
        {Sidebar}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setOpen(false)}
            data-testid="admin-mobile-overlay"
          />
          <aside
            className="lg:hidden fixed left-0 top-0 h-screen w-72 max-w-[85vw] z-50 flex flex-col p-5 shadow-2xl animate-in slide-in-from-left"
            style={{ background: "var(--sl-primary-dark)" }}
            data-testid="admin-mobile-sidebar"
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 p-2 rounded-lg text-white/70 hover:bg-white/10"
              aria-label="Fermer"
              data-testid="admin-mobile-menu-close"
            >
              <X size={18} />
            </button>
            {Sidebar}
          </aside>
        </>
      )}

      <main className="flex-1 min-w-0 lg:ml-64 p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
        <AdminAlerts />
        <Outlet />
      </main>
    </div>
  );
}
